import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { InvoiceBody, InvoiceView } from '@lemoncloud/lemon-billings-api';
import { Params } from '@lemoncloud/lemon-web-core';
import { useQuery } from '@tanstack/react-query';

import { PaginationType, useCustomMutation } from '@codes/shared';

import {
    cancelInvoice,
    createInvoices,
    deleteInvoice,
    fetchInvoiceById,
    fetchInvoices,
    makeInvoice,
    payInvoice,
    sendInvoiceEmail,
    updateInvoice,
} from '../apis';
import { invoicesKeys } from '../consts';
import { SendInvoiceDto, UpdateInvoiceDTO } from '../types';

export const useCreateInvoice = () => {
    return useCustomMutation((data: InvoiceBody) => createInvoices(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useMakeInvoice = () => {
    return useCustomMutation((data: UpdateInvoiceDTO) => makeInvoice(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useCancelInvoice = () => {
    return useCustomMutation((data: UpdateInvoiceDTO) => cancelInvoice(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useMakeAndPayInvoice = () => {
    return useCustomMutation(
        async (data: UpdateInvoiceDTO) => {
            const result = await makeInvoice(data);
            if (!result.payReceiptId) {
                throw new Error('Failed to make invoice: payReceiptId is missing');
            }

            return await payInvoice({ ...data, payReceiptId: result.payReceiptId });
        },
        {
            onError: error => {
                toast({ title: `${error.toString()}` });
            },
        }
    );
};

export const useInvoices = (params: Params) =>
    useQuery<PaginationType<InvoiceView[]>>({
        queryKey: invoicesKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchInvoices(params);
            return { ...result, data: result.list || [] } as PaginationType<InvoiceView[]>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
    });

export const useInvoice = (invoiceId: string) =>
    useQuery<InvoiceView>({
        queryKey: invoicesKeys.detail(invoiceId),
        queryFn: () => fetchInvoiceById(invoiceId),
        refetchOnWindowFocus: false,
        enabled: !!invoiceId,
        refetchOnMount: true,
        staleTime: 0,
    });

export const useUpdateInvoice = () => {
    return useCustomMutation((data: UpdateInvoiceDTO) => updateInvoice(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useSendInvoiceEmail = () => {
    return useCustomMutation((data: { invoiceId: string; body: SendInvoiceDto }) => sendInvoiceEmail(data), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};

export const useSendInvoiceEmailUsingBulk = () => {
    return useCustomMutation(
        async (data: { invoiceId: string; body: SendInvoiceDto }[]) => {
            const requests = data.map(({ invoiceId, body }) => sendInvoiceEmail({ invoiceId, body }));
            const responses = await Promise.all(requests);
            return responses;
        },
        {
            onError: error => {
                toast({ title: `${error.toString()}` });
            },
        }
    );
};

export const useDeleteInvoice = () => {
    return useCustomMutation((id: string) => deleteInvoice(id), {
        onError: error => {
            toast({ title: `${error.toString()}` });
        },
    });
};
